import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  MenuItem, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Chip, 
  IconButton, 
  InputAdornment, 
  FormControl, 
  Select, 
  InputLabel,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Fade,
  useTheme,
  useMediaQuery,
  Container,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  FormHelperText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  CloudUpload as UploadIcon, 
  CameraAlt as CameraIcon, 
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PhotoCamera as PhotoCameraIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import Camera from '../components/camera/Camera';

// Create an Axios instance with default configuration
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '', // Use environment variable or empty string
});

// Add a request interceptor to include the auth token in all requests
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Helper function to add delay between API calls
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const ImagePreview = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: '300px',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  margin: theme.spacing(2, 0),
}));

const ItemCard = styled(Card)(({ theme, saving }) => ({
  marginBottom: theme.spacing(3),
  position: 'relative',
  transition: 'all 0.3s ease',
  backgroundColor: saving ? 'rgba(25, 118, 210, 0.05)' : theme.palette.background.paper,
  border: saving ? `1px solid ${theme.palette.primary.main}` : 'none',
}));

const SavingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  borderRadius: theme.shape.borderRadius,
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

function OcrUploadPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeStep, setActiveStep] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [extractedItems, setExtractedItems] = useState([]);
  const [receiptInfo, setReceiptInfo] = useState({
    merchant: '',
    date: ''
  });
  const [showCamera, setShowCamera] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveResults, setSaveResults] = useState({ success: [], failed: [] });
  const [savingItemIndex, setSavingItemIndex] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  // Common categories and units for dropdown selection
  const commonCategories = [
    'Produce', 'Dairy', 'Meat', 'Seafood', 'Bakery', 'Pantry', 
    'Frozen', 'Beverages', 'Snacks', 'Household', 'Personal Care', 'Other'
  ];
  
  const commonUnits = [
    'pcs', 'kg', 'g', 'lb', 'oz', 'l', 'ml', 'gal', 'box', 'pack', 'bottle', 'can'
  ];

  // Get category color based on category name
  const getCategoryColor = (category) => {
    const categoryColors = {
      'Produce': 'success',
      'Dairy': 'info',
      'Meat': 'error',
      'Seafood': 'info',
      'Bakery': 'warning',
      'Pantry': 'secondary',
      'Frozen': 'primary',
      'Beverages': 'info',
      'Snacks': 'warning',
      'Household': 'default',
      'Personal Care': 'secondary',
      'Other': 'default'
    };
    
    return categoryColors[category] || 'default';
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      processSelectedFile(selectedFile);
    }
  };

  const processSelectedFile = (selectedFile) => {
    // Reset state
    setFile(selectedFile);
    setError('');
    setExtractedItems([]);
    setReceiptInfo({ merchant: '', date: '' });
    setSaveResults({ success: [], failed: [] });
    setSavingItemIndex(null);
    
    // Create a preview URL
    const fileReader = new FileReader();
    fileReader.onload = () => {
      setPreviewUrl(fileReader.result);
    };
    fileReader.readAsDataURL(selectedFile);
  };

  const handleCameraCapture = (imageDataUrl) => {
    // Convert data URL to File object
    fetch(imageDataUrl)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
        processSelectedFile(file);
        setShowCamera(false);
      });
  };

  const validateFile = (file) => {
    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG or PNG)');
      return false;
    }
    
    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setError('File is too large. Maximum size is 5MB');
      return false;
    }
    
    return true;
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    
    if (!validateFile(file)) {
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setError('');
    
    // Create form data
    const formData = new FormData();
    formData.append('image', file);
    formData.append('is_receipt', 'true'); // Indicate this is a receipt for the backend
    
    try {
      // Upload the file with progress tracking
      const response = await API.post('/api/receipt/process', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });
      
      // Process extracted items
      if (response.data.results && response.data.results.detected_fields && 
          response.data.results.detected_fields.items && 
          response.data.results.detected_fields.items.length > 0) {
        
        // Get receipt information
        const result = response.data.results;
        setReceiptInfo({
          merchant: result.merchant || '',
          date: result.transaction_date || ''
        });
        
        // Transform the items into a format suitable for inventory
        const items = response.data.results.detected_fields.items.map(item => {
          // Determine a default category based on the product name
          let defaultCategory = 'Other';
          const productName = (item.product_name || '').toLowerCase();
          
          if (productName.includes('milk') || productName.includes('cheese') || productName.includes('yogurt')) {
            defaultCategory = 'Dairy';
          } else if (productName.includes('bread') || productName.includes('cake') || productName.includes('pastry')) {
            defaultCategory = 'Bakery';
          } else if (productName.includes('apple') || productName.includes('banana') || productName.includes('fruit') || 
                     productName.includes('vegetable')) {
            defaultCategory = 'Produce';
          } else if (productName.includes('chicken') || productName.includes('beef') || productName.includes('pork')) {
            defaultCategory = 'Meat';
          } else if (productName.includes('water') || productName.includes('soda') || productName.includes('juice')) {
            defaultCategory = 'Beverages';
          }
          
          return {
            item_name: item.product_name || '',
            quantity: item.quantity ? parseFloat(item.quantity) : 1,
            unit_of_measure: item.unit || 'pcs',
            category: defaultCategory,
            description: '',
            purchase_date: item.purchase_date || result.transaction_date || '',
            expiry_date: item.expiry_date || '',
            location: '',
            low_stock_threshold: '',
            brand: item.brand || result.merchant || '',
            notes: `Extracted from receipt using Document Intelligence on ${new Date().toLocaleDateString()}`
          };
        });
        
        setExtractedItems(items);
        setActiveStep(1); // Move to the next step
        setNotification({
          type: 'success',
          message: `Successfully extracted ${items.length} items from your receipt!`
        });
      } else {
        // If no items were extracted, show a message
        setError('No items could be extracted from the receipt. Please try a clearer image or add items manually.');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(
        error.response?.data?.message || 
        'Failed to process the receipt. Please try again or add items manually.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...extractedItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setExtractedItems(updatedItems);
  };

  const handleAddNewItem = () => {
    // Add a new empty item to the list
    setExtractedItems([
      ...extractedItems,
      {
        item_name: '',
        quantity: 1,
        unit_of_measure: 'pcs',
        category: 'Other',
        description: '',
        purchase_date: receiptInfo.date || '',
        expiry_date: '',
        location: '',
        low_stock_threshold: '',
        brand: receiptInfo.merchant || '',
        notes: `Added manually from receipt on ${new Date().toLocaleDateString()}`
      }
    ]);
  };

  const handleSaveItem = async (item, index) => {
    try {
      // Validate required fields
      if (!item.item_name || !item.category || !item.quantity || !item.unit_of_measure) {
        setNotification({
          type: 'error',
          message: 'Please fill in all required fields (name, category, quantity, unit)'
        });
        return;
      }
      
      // Prepare data for submission
      const submitData = { ...item };
      
      // Convert string numbers to actual numbers
      if (submitData.quantity) submitData.quantity = Number(submitData.quantity);
      if (submitData.low_stock_threshold) submitData.low_stock_threshold = Number(submitData.low_stock_threshold);
      
      // Submit the item to the backend
      await API.post('/api/inventory', submitData);
      
      // Remove the item from the list
      const updatedItems = extractedItems.filter((_, i) => i !== index);
      setExtractedItems(updatedItems);
      
      setNotification({
        type: 'success',
        message: `${item.item_name} has been added to your inventory!`
      });
      
      // If no more items, redirect to inventory dashboard
      if (updatedItems.length === 0) {
        navigate('/inventory');
      }
    } catch (error) {
      console.error('Error saving item:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Failed to add inventory item. Please try again.'
      });
    }
  };

  const handleSaveAllItems = async () => {
    try {
      // Validate all items
      for (const item of extractedItems) {
        if (!item.item_name || !item.category || !item.quantity || !item.unit_of_measure) {
          setNotification({
            type: 'error',
            message: 'Please fill in all required fields for all items (name, category, quantity, unit)'
          });
          return;
        }
      }
      
      setIsSaving(true);
      setSaveProgress(0);
      setSaveResults({ success: [], failed: [] });
      
      // Save items one by one sequentially with a delay between each save
      const totalItems = extractedItems.length;
      const successfulItems = [];
      const failedItems = [];
      
      // Use a for loop instead of Promise.all to save items sequentially with delay
      for (let i = 0; i < totalItems; i++) {
        const item = extractedItems[i];
        const submitData = { ...item };
        
        // Set the current saving item index for UI feedback
        setSavingItemIndex(i);
        
        // Convert string numbers to actual numbers
        if (submitData.quantity) submitData.quantity = Number(submitData.quantity);
        if (submitData.low_stock_threshold) submitData.low_stock_threshold = Number(submitData.low_stock_threshold);
        
        try {
          console.log(`Saving item ${i + 1}/${totalItems}: ${submitData.item_name}`);
          
          // Save the item and wait for the response
          const response = await API.post('/api/inventory', submitData);
          
          // Add a delay between saves to prevent throttling on the backend
          await delay(800); // 800ms delay between saves
          
          // Add to successful items
          successfulItems.push({
            ...submitData,
            item_id: response.data.item?.item_id
          });
          
          // Update progress
          const newProgress = Math.round(((i + 1) / totalItems) * 100);
          setSaveProgress(newProgress);
          
          // Log success for debugging
          console.log(`Successfully saved item ${i + 1}/${totalItems}: ${submitData.item_name}`);
          
        } catch (error) {
          console.error(`Error saving item ${i + 1}/${totalItems}:`, error);
          
          // Add to failed items
          failedItems.push({
            item: submitData,
            error: error.response?.data?.message || 'Unknown error'
          });
          
          // Update progress even for failed items
          const newProgress = Math.round(((i + 1) / totalItems) * 100);
          setSaveProgress(newProgress);
          
          // Still add a delay even after failure
          await delay(800);
        }
      }
      
      // Reset saving item index
      setSavingItemIndex(null);
      
      // Update save results
      setSaveResults({
        success: successfulItems,
        failed: failedItems
      });
      
      // Show error if any items failed to save
      if (failedItems.length > 0) {
        setNotification({
          type: 'warning',
          message: `Failed to save ${failedItems.length} item(s). ${successfulItems.length} item(s) were saved successfully.`
        });
        setIsSaving(false);
        return;
      }
      
      // If all items were saved successfully, navigate to inventory
      console.log(`All ${successfulItems.length} items saved successfully. Navigating to inventory...`);
      
      // Add a longer delay to ensure all items are properly saved before navigating
      setTimeout(() => {
        // Store the saved item IDs in localStorage to ensure they're visible in the inventory
        localStorage.setItem('lastSavedItems', JSON.stringify(successfulItems.map(item => item.item_id)));
        
        // Force a reload of the page to ensure fresh data
        localStorage.setItem('forceInventoryRefresh', 'true');
        
        // Redirect to inventory dashboard after success with a refresh flag
        navigate('/inventory', { 
          state: { 
            refresh: true,
            fromReceipt: true,
            itemCount: successfulItems.length
          } 
        });
      }, 2000); // Increased to 2 seconds
      
    } catch (error) {
      console.error('Error in save all process:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Failed to add inventory items. Please try again.'
      });
      setIsSaving(false);
    }
  };

  // If camera is open, render the Camera component
  if (showCamera) {
    return (
      <Camera 
        onCapture={handleCameraCapture} 
        onClose={() => setShowCamera(false)} 
      />
    );
  }

  return (
    <Container maxWidth="lg">
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert 
          onClose={() => setNotification(null)} 
          severity={notification?.type || 'info'} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
      
      <Dialog
        open={showInfoDialog}
        onClose={() => setShowInfoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>About Receipt Scanning</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Our receipt scanning feature uses advanced Document Intelligence to extract item information from your receipts.
          </Typography>
          <Typography paragraph>
            For best results:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Box component="li" sx={{ mb: 1 }}>
              Ensure the receipt is well-lit and the text is clearly visible
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              Capture the entire receipt in the frame
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              Avoid shadows and glare on the receipt
            </Box>
            <Box component="li">
              After scanning, you can review and edit the extracted items before adding them to your inventory
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInfoDialog(false)} color="primary">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
      
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon 
              sx={{ 
                fontSize: 36, 
                mr: 2, 
                color: 'primary.main' 
              }} 
            />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Scan Receipt
            </Typography>
          </Box>
          
          <Tooltip title="Learn about receipt scanning">
            <IconButton 
              color="primary" 
              onClick={() => setShowInfoDialog(true)}
              size="small"
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Upload a photo of your receipt to quickly add items to your inventory.
          Our system will extract item details automatically using Document Intelligence.
        </Typography>
        
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel
          sx={{ 
            mb: 4,
            '& .MuiStepLabel-label': {
              mt: 1
            }
          }}
        >
          <Step>
            <StepLabel>Upload Receipt</StepLabel>
          </Step>
          <Step>
            <StepLabel>Review Items</StepLabel>
          </Step>
          <Step>
            <StepLabel>Add to Inventory</StepLabel>
          </Step>
        </Stepper>
      </Box>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
      
      {activeStep === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 2, sm: 4 }, 
              borderRadius: 2,
              mb: 4
            }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <UploadCard 
                  component={Button}
                  variant="outlined"
                  color="primary"
                  sx={{ 
                    height: '100%', 
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  component="label"
                >
                  <UploadIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
                  <Typography variant="h6" gutterBottom>
                    Upload Receipt Image
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select a JPEG or PNG image (max 5MB)
                  </Typography>
                  <VisuallyHiddenInput 
                    type="file"
                    accept="image/jpeg, image/png"
                    onChange={handleFileChange}
                  />
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<UploadIcon />}
                    component="span"
                  >
                    Choose File
                  </Button>
                  {file && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mt: 2, 
                        fontWeight: 'medium',
                        color: 'primary.main'
                      }}
                    >
                      {file.name}
                    </Typography>
                  )}
                </UploadCard>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <UploadCard 
                  onClick={() => setShowCamera(true)}
                  sx={{ 
                    height: '100%', 
                    minHeight: 200,
                    cursor: 'pointer'
                  }}
                >
                  <CameraIcon sx={{ fontSize: 48, mb: 2, color: 'secondary.main' }} />
                  <Typography variant="h6" gutterBottom>
                    Take a Photo
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Use your device's camera to capture the receipt
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="secondary"
                    startIcon={<PhotoCameraIcon />}
                  >
                    Open Camera
                  </Button>
                </UploadCard>
              </Grid>
            </Grid>
            
            {previewUrl && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Receipt Preview
                </Typography>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    display: 'inline-block',
                    maxWidth: '100%',
                    backgroundColor: 'grey.100'
                  }}
                >
                  <ImagePreview 
                    src={previewUrl} 
                    alt="Receipt preview" 
                  />
                </Paper>
              </Box>
            )}
            
            {isUploading ? (
              <Box sx={{ mt: 4 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Processing receipt... {uploadProgress}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Our AI is analyzing your receipt and extracting item information...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                mt: 4, 
                display: 'flex', 
                justifyContent: 'space-between'
              }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate('/inventory')}
                  startIcon={<ArrowBackIcon />}
                >
                  Back to Inventory
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpload}
                  disabled={!file}
                  endIcon={<ArrowForwardIcon />}
                >
                  Process Receipt
                </Button>
              </Box>
            )}
          </Paper>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 2, sm: 4 }, 
              borderRadius: 2,
              mb: 4
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Review Extracted Items
              </Typography>
              
              {(receiptInfo.merchant || receiptInfo.date) && (
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    mb: 3, 
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2
                  }}
                >
                  {receiptInfo.merchant && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
                        Merchant:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {receiptInfo.merchant}
                      </Typography>
                    </Box>
                  )}
                  
                  {receiptInfo.date && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
                        Date:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(receiptInfo.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {extractedItems.length} {extractedItems.length === 1 ? 'item' : 'items'} found
                </Typography>
                
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddNewItem}
                  size="small"
                >
                  Add Item Manually
                </Button>
              </Box>
            </Box>
            
            {extractedItems.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                No items found in the receipt. You can add items manually using the button above.
              </Alert>
            ) : (
              <Stack spacing={3}>
                {extractedItems.map((item, index) => (
                  <ItemCard 
                    key={index} 
                    elevation={2}
                    saving={savingItemIndex === index}
                  >
                    {savingItemIndex === index && (
                      <SavingOverlay>
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress size={40} sx={{ mb: 1 }} />
                          <Typography variant="body2" fontWeight="medium">
                            Saving item...
                          </Typography>
                        </Box>
                      </SavingOverlay>
                    )}
                    
                    <CardContent>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2
                      }}>
                        <Typography variant="h6" component="h3">
                          Item {index + 1}
                        </Typography>
                        
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => {
                            const updatedItems = extractedItems.filter((_, i) => i !== index);
                            setExtractedItems(updatedItems);
                            if (updatedItems.length === 0) {
                              setActiveStep(0);
                            }
                          }}
                          aria-label="Remove item"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Item Name"
                            fullWidth
                            value={item.item_name}
                            onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}
                            required
                            margin="dense"
                            error={!item.item_name}
                            helperText={!item.item_name ? "Item name is required" : ""}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth margin="dense" error={!item.category}>
                            <InputLabel id={`category-label-${index}`}>Category</InputLabel>
                            <Select
                              labelId={`category-label-${index}`}
                              value={item.category}
                              onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                              label="Category"
                              required
                            >
                              {commonCategories.map(category => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))}
                            </Select>
                            {!item.category && (
                              <FormHelperText>Category is required</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Quantity"
                            type="number"
                            fullWidth
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            required
                            margin="dense"
                            InputProps={{ inputProps: { min: 0, step: "any" } }}
                            error={!item.quantity}
                            helperText={!item.quantity ? "Required" : ""}
                          />
                        </Grid>
                        
                        <Grid item xs={6} sm={3}>
                          <FormControl fullWidth margin="dense" error={!item.unit_of_measure}>
                            <InputLabel id={`unit-label-${index}`}>Unit</InputLabel>
                            <Select
                              labelId={`unit-label-${index}`}
                              value={item.unit_of_measure}
                              onChange={(e) => handleItemChange(index, 'unit_of_measure', e.target.value)}
                              label="Unit"
                              required
                            >
                              {commonUnits.map(unit => (
                                <MenuItem key={unit} value={unit}>
                                  {unit}
                                </MenuItem>
                              ))}
                            </Select>
                            {!item.unit_of_measure && (
                              <FormHelperText>Required</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Brand"
                            fullWidth
                            value={item.brand || ''}
                            onChange={(e) => handleItemChange(index, 'brand', e.target.value)}
                            margin="dense"
                            placeholder="e.g., Kraft, Nestle"
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Storage Location"
                            fullWidth
                            value={item.location || ''}
                            onChange={(e) => handleItemChange(index, 'location', e.target.value)}
                            margin="dense"
                            placeholder="e.g., Pantry, Fridge"
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Purchase Date"
                            type="date"
                            fullWidth
                            value={item.purchase_date || ''}
                            onChange={(e) => handleItemChange(index, 'purchase_date', e.target.value)}
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Expiry Date"
                            type="date"
                            fullWidth
                            value={item.expiry_date || ''}
                            onChange={(e) => handleItemChange(index, 'expiry_date', e.target.value)}
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            label="Description"
                            fullWidth
                            value={item.description || ''}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            margin="dense"
                            multiline
                            rows={2}
                          />
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 2 }}>
                        <CategoryChip 
                          label={item.category} 
                          size="small" 
                          color={getCategoryColor(item.category)}
                        />
                        {item.brand && (
                          <CategoryChip 
                            label={item.brand} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </CardContent>
                    
                    <Divider />
                    
                    <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSaveItem(item, index)}
                        disabled={!item.item_name || !item.category || !item.quantity || !item.unit_of_measure}
                      >
                        Add to Inventory
                      </Button>
                    </CardActions>
                  </ItemCard>
                ))}
              </Stack>
            )}
            
            {isSaving ? (
              <Box sx={{ mt: 4 }}>
                <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                  Saving items to inventory... {saveProgress}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={saveProgress} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 2
                }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Please wait while all items are being saved...
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                mt: 4, 
                display: 'flex', 
                justifyContent: 'space-between'
              }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setActiveStep(0)}
                  startIcon={<ArrowBackIcon />}
                >
                  Back
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveAllItems}
                  disabled={extractedItems.length === 0}
                  endIcon={<CheckCircleIcon />}
                >
                  Add All Items to Inventory
                </Button>
              </Box>
            )}
          </Paper>
        </motion.div>
      )}
    </Container>
  );
}

export default OcrUploadPage;